/**
 * `WooCommerceInstallPanel` — body of the `woocommerce` install-method
 * tab inside `InstallMethodTabs`. Split from `WordPressInstallPanel` on
 * 2026-07-08 (founder feedback on live onboarding): a combined
 * "WordPress / WooCommerce" tab conflated two separate installation
 * paths. WordPress is script-only; WooCommerce is a two-step commit —
 * the discovery + domain-proof snippet (Step 1) AND the REST-API
 * connect (Step 2). Both are named + numbered so the merchant knows
 * exactly what's happening at each step.
 *
 * Composition (top → bottom):
 *   - Step 1 header — `STEP 1 · DISCOVERY + DOMAIN PROOF`. One-sentence
 *     rationale ("The script tag advertises your signed agent card to
 *     visiting agents and proves you own the domain."), then the
 *     `WordPressScriptSnippetBlock` (same snippet + WP placement copy +
 *     rotate card + `InstallDetector` the WP panel uses).
 *   - Step 2 header — `STEP 2 · CONNECT THE STORE`. One-sentence
 *     rationale ("The REST API key lets agents read orders and
 *     recommendations, keeps your catalog synced, and enables governed
 *     checkout once Stripe is connected."), then
 *     `WooCommerceInstructions` + `WooCommerceConnectForm` with
 *     `idPrefix="onboarding-woo"` (unchanged from the prior shared
 *     tab). Post-connect the panel shows the canonicalized store URL
 *     the same way as before.
 *
 * Sequencing invariant (unchanged, ADR-0170 §2 + this panel):
 *   the http-challenge P1 proof (Step 1's Verify — rendered above the
 *   tabs in `InstallVerifyStep`) and the `woocommerce_keys` proof
 *   (Step 2) are SEPARATE proofs. Both must land to unlock order-read
 *   composition. The "Complete Step 1's verify above" note under
 *   Step 2 names this explicitly so a merchant who sees "Connected"
 *   here doesn't assume Step 1 became optional.
 */

import { ArrowRight, Check, Package } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';

import { useSites } from '../../app/providers.js';
import { useC } from '../../lib/theme.js';
import type { UseProofCheckState } from '../../hooks/useProofCheck.js';
import { Collapsible, StepHeading } from '../../components/atoms/index.js';
import {
  WooCommerceConnectForm,
  WooCommerceInstructions,
} from '../shared/WooCommerceConnect.js';
import { AgentReadyPluginDownload } from './AgentReadyPluginDownload.js';
import { PLUGIN_SENTENCE_WOO } from './install-copy.js';
const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

export interface WooCommerceInstallPanelProps {
  /** ADR-0212 slice C — when set, Step 1 is the Worker (orange-clouded site). */
  workerStep?: ReactNode;
  onPreferWorker?: () => void;
  /** Retained for caller compatibility (InstallMethodTabs spreads it);
   *  the Step-1 script surface now lives on the Script tab. */
  onInstalled?: () => void;
  /** Retained for caller compatibility. */
  proofCheck?: UseProofCheckState;
  /** Switch the picker to the Script tag tab — the "can't install a
   *  plugin" fallback (founder 2026-07-29, no inline snippet duplication). */
  onSwitchToScriptTab?: () => void;
}

export function WooCommerceInstallPanel({
  workerStep,
  onPreferWorker,
  onSwitchToScriptTab,
}: WooCommerceInstallPanelProps): JSX.Element {
  const C = useC();
  const { activeSite } = useSites();
  // Local to this panel (moved with the split from `WordPressInstallPanel`
  // 2026-07-08). Post-connect display uses the canonicalized URL the
  // POST /connector/woocommerce response echoes back; a remount pre-
  // verify falls back to the pill-only view.
  const [wooConnectedUrl, setWooConnectedUrl] = useState<string | null>(null);

  return (
    <div>
      {/* STEP 1 — the plugin (ADR-0212: kicker + one sentence + button;
          the how-to sits in the block's own disclosure). */}
      {workerStep !== undefined ? workerStep : (
        <AgentReadyPluginDownload kicker="STEP 1 · INSTALL THE SILL PLUGIN" sentence={PLUGIN_SENTENCE_WOO} />
      )}
      {/* Alternatives for Step 1 (same row + style as WordPress). */}
      {onPreferWorker !== undefined || onSwitchToScriptTab !== undefined ? (
        <div
          className="flex flex-wrap items-center"
          style={{ gap: '0 10px', marginTop: 12 }}
        >
          {onPreferWorker !== undefined ? (
            <button
              type="button"
              onClick={onPreferWorker}
              className="inline-flex items-center gap-1"
              style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontFamily: sansFont, fontSize: 13, color: C.muted }}
              data-testid="woo-prefer-worker"
            >
              On Cloudflare?{' '}
              <span style={{ color: C.text, fontWeight: 500 }}>Use the Worker</span>
              <ArrowRight size={12} />
            </button>
          ) : null}
          {onPreferWorker !== undefined && onSwitchToScriptTab !== undefined ? (
            <span style={{ fontFamily: sansFont, fontSize: 13, color: C.dim }}>·</span>
          ) : null}
          {onSwitchToScriptTab !== undefined ? (
            <button
              type="button"
              onClick={onSwitchToScriptTab}
              className="inline-flex items-center gap-1"
              style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontFamily: sansFont, fontSize: 13, color: C.muted }}
              data-testid="woo-fallback-script-tab"
            >
              Can&apos;t use a plugin?{' '}
              <span style={{ color: C.text, fontWeight: 500 }}>Use the script tag</span>
              <ArrowRight size={12} />
            </button>
          ) : null}
        </div>
      ) : null}

      {/* --------------------------------------------------------------
          STEP 2 — Connect the store (WooCommerce REST API)
          -------------------------------------------------------------- */}
      {activeSite !== null ? (
        <div
          className="mt-5 pt-5"
          style={{ borderTop: `1px solid ${C.borderSoft}` }}
          data-testid="onboarding-woo-connect"
        >
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <StepHeading n={2}>Connect the store</StepHeading>
            {wooConnectedUrl !== null ? (
              <span
                className="inline-flex items-center gap-1 px-1.5"
                style={{
                  background: C.limeDim,
                  color: C.limeFg,
                  border: `1px solid ${C.limeFg}`,
                  borderRadius: 2,
                  fontFamily: monoFont,
                  fontSize: 10,
                  letterSpacing: 1.5,
                  paddingTop: 2,
                  paddingBottom: 2,
                }}
              >
                <Check size={10} /> CONNECTED
              </span>
            ) : (
              <span
                className="inline-flex items-center gap-1 px-1.5"
                style={{
                  background: 'transparent',
                  color: C.muted,
                  border: `1px solid ${C.border}`,
                  borderRadius: 2,
                  fontFamily: monoFont,
                  fontSize: 10,
                  letterSpacing: 1.5,
                  paddingTop: 2,
                  paddingBottom: 2,
                }}
              >
                <Package size={10} /> WOOCOMMERCE
              </span>
            )}
          </div>
          <div
            style={{
              fontFamily: serifFont,
              fontSize: 24,
              color: C.text,
              letterSpacing: -0.5,
              marginBottom: 6,
            }}
          >
            Connect WooCommerce
          </div>
          <p
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.text,
              lineHeight: 1.55,
              marginBottom: 14,
              maxWidth: 560,
            }}
          >
            Connecting lets agents read your catalog and orders. Checkout
            activates later, once Stripe is connected.
          </p>
          {wooConnectedUrl !== null ? (
            <div
              className="mb-3 p-3"
              style={{
                background: C.surface2,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
              }}
            >
              <div
                style={{
                  fontFamily: monoFont,
                  fontSize: 9,
                  color: C.muted,
                  letterSpacing: 1,
                  fontWeight: 600,
                  marginBottom: 4,
                }}
              >
                STORE URL
              </div>
              <div
                style={{
                  fontFamily: monoFont,
                  fontSize: 12,
                  color: C.text,
                  wordBreak: 'break-all',
                }}
              >
                {wooConnectedUrl}
              </div>
            </div>
          ) : (
            <>
              {/* Founder 2026-07-14: the step-by-step REST-API key
                  instructions were too much text inline. Tucked behind an
                  Instructions toggle; the connect form stays visible. */}
              <div className="mb-3">
                <Collapsible variant="inline" label="API key steps">
                  <WooCommerceInstructions C={C} />
                </Collapsible>
              </div>
              <WooCommerceConnectForm
                C={C}
                site={activeSite}
                onConnected={setWooConnectedUrl}
                idPrefix="onboarding-woo"
              />
            </>
          )}
          {/* Sequencing note — the http-challenge P1 proof (Step 1's
              Verify, rendered above the tabs in `InstallVerifyStep`)
              is a SEPARATE proof from the `woocommerce_keys` proof.
              Both compose to unlock order reads; either alone is
              insufficient. The merchant must NOT read "Connected"
              here and assume Step 1's verify became optional. */}
          <div
            className="mt-3"
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              color: C.muted,
              lineHeight: 1.5,
              maxWidth: 560,
            }}
          >
            Step 1&apos;s Verify is still required — order reads unlock once
            both are done.
          </div>
        </div>
      ) : null}
    </div>
  );
}
